import { useState, useEffect } from "react";
import { Edit, Trash2, Plus } from "lucide-react";
import api from "../../api/api";
import AdminTourForm from "./AdminTourForm";

export default function AdminTours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    setLoading(true);
    try {
      const res = await api.get("/tours?limit=100");
      setTours(res.data.tours);
    } catch (error) {
      console.error("Error fetching tours:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tourId) => {
    if (window.confirm("Are you sure you want to delete this tour?")) {
      try {
        await api.delete(`/tours/${tourId}`);
        setTours(tours.filter((tour) => tour.id !== tourId));
      } catch (error) {
        console.error("Error deleting tour:", error);
      }
    }
  };

  const handleSaveTour = () => {
    fetchTours();
    setShowForm(false);
    setEditingTour(null);
  };

  if (showForm) {
    return (
      <AdminTourForm
        tour={editingTour}
        onSave={handleSaveTour}
        onCancel={() => {
          setShowForm(false);
          setEditingTour(null);
        }}
      />
    );
  }

  return (
    <div className="">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white tracking-tight">
          Manage Tours
        </h2>

        <button
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 
          text-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg hover:scale-[1.02] 
          transition-all duration-200"
          onClick={() => {
            setEditingTour(null);
            setShowForm(true);
          }}
        >
          <Plus size={20} />
          Add New Tour
        </button>
      </div>

      {/* LOADING */}
      {loading ? (
        <div className="text-center text-gray-500 dark:text-gray-300 text-lg py-20">
          Loading tours...
        </div>
      ) : (
        // GRID CARDS
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {tours.map((tour) => (
            <div
              key={tour.id}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-md border 
              border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-xl 
              hover:-translate-y-1 transition-all duration-300"
            >
              {/* IMAGE */}
              <div className="h-48 w-full overflow-hidden">
                <img
                  src={`https://backend.ysskytravel.com/uploads/tours/${tour.image_url}`}
                  alt={tour.title}
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>

              {/* CONTENT */}
              <div className="p-4 space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {tour.title}
                </h3>

                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {tour.location}
                </p>

                <p className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                  $ {tour.discount_price}
                </p>

                {/* BUTTONS */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => {
                      setEditingTour(tour);
                      setShowForm(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 
                    bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded-lg 
                    transition duration-200 shadow-sm"
                  >
                    <Edit size={16} />
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(tour.id)}
                    className="flex-1 flex items-center justify-center gap-1 
                    bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg 
                    transition duration-200 shadow-sm"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
